import { BdcBranch } from "../../../types/bdc.type";

/** Selector value for "every branch I can see" — sends no company_id. */
export const ALL_BRANCHES = "all";

/**
 * Chip colors per BDC branch, by its place in the code-sorted list so a branch keeps one color
 * on every screen (prototype: กรุงเทพ charcoal, ระยอง amber).
 */
const BRANCH_COLORS = [
  { bg: "#363A44", fg: "#FFFFFF" },
  { bg: "#FDA42C", fg: "#22252C" },
  { bg: "#2F5DA8", fg: "#FFFFFF" },
  { bg: "#1E8E5A", fg: "#FFFFFF" },
] as const;
const UNKNOWN_COLOR = { bg: "#EEF0F3", fg: "#6B7080" } as const;

export const sortBranches = (branches: BdcBranch[]): BdcBranch[] =>
  branches
    .slice()
    .sort(
      (a, b) =>
        (a.branch_code ?? "").localeCompare(b.branch_code ?? "") ||
        a.company_id.localeCompare(b.company_id),
    );

/** `82-DB` — falls back to the company_id when the GDP branch has no code. */
export const branchCodeOf = (branch: Pick<BdcBranch, "company_id" | "branch_code">): string =>
  branch.branch_code || `#${branch.company_id}`;

/** `82-DB BDC - กรุงเทพ` */
export const branchLabel = (branch: BdcBranch): string =>
  [branchCodeOf(branch), branch.branch_name].filter(Boolean).join(" ");

export const branchColor = (branches: BdcBranch[], companyId: string | null | undefined) => {
  if (!companyId) return UNKNOWN_COLOR;
  const index = sortBranches(branches).findIndex((b) => b.company_id === companyId);
  return index < 0 ? UNKNOWN_COLOR : BRANCH_COLORS[index % BRANCH_COLORS.length];
};

/**
 * The branches a view covers: the picked one, or every visible branch under "ทุกสาขา".
 * A stale pick (no longer visible) reads as "ทุกสาขา".
 */
export const viewedBranches = (branches: BdcBranch[], selected: string): BdcBranch[] => {
  const one = branches.find((b) => b.company_id === selected);
  return one ? [one] : branches;
};

/** company_id to send, or undefined for ทุกสาขา / a pick that is not (or no longer) visible. */
export const companyIdFilter = (branches: BdcBranch[], selected: string): string | undefined =>
  branches.some((b) => b.company_id === selected) ? selected : undefined;

// ---- remembered selection, per viewer ----

const STORAGE_PREFIX = "gdp.bdc.branch";

export const branchStorageKey = (viewerId: string | null | undefined) =>
  `${STORAGE_PREFIX}:${viewerId || "anonymous"}`;

/** localStorage may be absent or throw (private window, blocked site data) — then nothing is remembered. */
export const readStoredBranch = (viewerId: string | null | undefined): string => {
  try {
    return window.localStorage.getItem(branchStorageKey(viewerId)) || ALL_BRANCHES;
  } catch {
    return ALL_BRANCHES;
  }
};

export const writeStoredBranch = (viewerId: string | null | undefined, value: string): void => {
  try {
    if (value === ALL_BRANCHES) window.localStorage.removeItem(branchStorageKey(viewerId));
    else window.localStorage.setItem(branchStorageKey(viewerId), value);
  } catch {
    // not remembered; the pick still applies to this page
  }
};
